import { preventOneWordInLastLine } from '@lemonade-hq/cdk';
import type { Seconds } from '@lemonade-hq/ts-helpers';
import * as Toast from '@radix-ui/react-toast';
import { assignInlineVars } from '@vanilla-extract/dynamic';
import { motion } from 'framer-motion';
import React from 'react';
import { spacing } from '../../styles/spacing.css';
import { Flex } from '../Flex/Flex';
import { Icon } from '../Icon/Icon';
import type { IconName } from '../Icon/Icon';
import { LinkButton } from '../LinkButton/LinkButton';
import { Text } from '../Text/Text';
import * as styles from './Toast.css';
import { useToastStack } from './useToastStack';

const ANIMATION_DURATION: Seconds = 0.4;
const INITIAL_Y_POSITION = 20;

export interface ToastAction {
  readonly label: string;
  readonly onAction: () => void;
}

export interface ToastItemData {
  readonly icon?: IconName;
  readonly title: string;
  readonly action?: ToastAction;
  readonly style?: React.HTMLAttributes<HTMLDivElement>['style'];
  readonly type?: 'attention' | 'success' | 'system';
  readonly id: string;
}

interface ToastItemProps {
  readonly toast: ToastItemData;
  readonly index: number;
  readonly totalToasts: number;
  readonly expanded: boolean;
  readonly onClose: (id: string) => void;
}

export const ToastItemComponent = React.forwardRef<HTMLDivElement, ToastItemProps>(
  ({ toast, index, totalToasts, expanded, onClose }, forwardedRef) => {
    const { shouldStack, shouldCollapse, toastWidth, shouldShowContent } = useToastStack({
      index,
      totalToasts,
      expanded,
    });

    return (
      <Toast.Root
        asChild
        className={styles.toastRoot({ [toast.type ?? 'system']: true })}
        duration={Infinity}
        onOpenChange={isOpen => {
          /* istanbul ignore else -- @preserve - Radix UI internal: toast opening state doesn't require action */
          if (!isOpen) onClose(toast.id);
        }}
        open
        style={{
          ...toast.style,
          ...assignInlineVars({
            [styles.toastIndexVar]: String(index),
            [styles.toastOrderVar]: String(!shouldCollapse ? index : totalToasts - index),
          }),
        }}
      >
        <motion.div
          animate={{
            opacity: 1,
            y: 0,
          }}
          className={shouldStack ? styles.toastWrapperStacked : styles.toastWrapper}
          exit={{
            opacity: 0,
            y: '-100%',
            transition: {
              default: { type: 'spring', stiffness: 400, damping: 30, mass: 1 },
              duration: ANIMATION_DURATION,
            },
          }}
          initial={{ opacity: 0, y: totalToasts > 1 ? INITIAL_Y_POSITION : 0 }}
          layoutId={toast.id}
          ref={forwardedRef}
          style={{
            zIndex: totalToasts - index,
            width: toastWidth,
          }}
          transition={{
            default: { type: 'spring', stiffness: 400, damping: 30, mass: 1 },
            duration: ANIMATION_DURATION,
          }}
        >
          <Flex as="li" gap={spacing.s12}>
            <Toast.Title asChild>
              <Flex flex={1} gap={spacing.s12}>
                {toast.icon != null && shouldShowContent && (
                  <Icon
                    containerClassName={styles.containerIcon}
                    name={toast.icon}
                    size="md"
                    variant={toast.type === 'success' || toast.type === 'attention' ? toast.type : 'inverted'}
                  />
                )}
                {toast.title && shouldShowContent && (
                  <Text type="bodySmall">{preventOneWordInLastLine(toast.title)}</Text>
                )}
              </Flex>
            </Toast.Title>
            {toast.action != null && shouldShowContent && (
              <Toast.Action altText={toast.action.label} asChild className="ToastAction">
                <LinkButton
                  aria-label={toast.action.label}
                  onClick={() => {
                    toast.action?.onAction();
                  }}
                  variant={
                    toast.type === 'success' || toast.type === 'attention'
                      ? ('inherit' as const)
                      : ('inverted' as const)
                  }
                >
                  {toast.action.label}
                </LinkButton>
              </Toast.Action>
            )}
          </Flex>
        </motion.div>
      </Toast.Root>
    );
  },
);

ToastItemComponent.displayName = 'ToastItem';
